import { useSidebarState } from '@src/hooks/useSidebarState';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import ContentStudioIcon from '@src/svgs/ContentStudioIcon';
import EditVideoIcon from '@src/svgs/EditVideoIcon';
import { Studio } from '@src/types.ts/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { AnimatePresence, motion as fmMotion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from './common/tooltipStyles';
import { SIDEBAR_SPRING } from './sidebar/sidebarMotion';
import { SidebarTooltip } from './SidebarTooltip';

import type { Story } from '@src/types.ts/story';

interface SidebarActiveStoryCardProps {
  story: Story | null;
  collapsed: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const SidebarActiveStoryCard: React.FC<SidebarActiveStoryCardProps> =
  observer(({ story, collapsed }) => {
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const { setCollapsed } = useSidebarState();
    const [currentStoryOpen, setCurrentStoryOpen] = useState(true);

    /* When navigating to Creator Studio, collapse the sidebar so it smoothly
     * animates to 0 width (the creator+collapsed hide rule kicks in). */
    const handleVideoEditorClick = () => {
      setCollapsed(true);
    };
    const { enableCreatorStudioNav } = useFlagsCombination(
      userIdentity.currentRole,
    );
    const { search } = useLocation();

    const params = new URLSearchParams(search);
    const env = params.get('env');

    const videoId =
      videoCreator.currentVideo?.id ?? params.get('videoId') ?? undefined;

    const buildStoryParams = (extra?: Record<string, string>) => {
      const p = new URLSearchParams();
      /* Routing requires at least one of { storyId, showcase } downstream.
       * - Preserve the URL's `showcase` (top-level context) whenever it's set.
       * - Prefer the loaded story's id; fall back to the URL's `storyId`
       *   while the store is still hydrating. */
      const showcaseFromUrl = params.get('showcase');
      if (showcaseFromUrl) p.set('showcase', showcaseFromUrl);
      if (story) {
        p.set('storyId', story.id);
      } else {
        const storyIdFromUrl = params.get('storyId');
        if (storyIdFromUrl) p.set('storyId', storyIdFromUrl);
      }
      if (env) p.set('env', env);
      if (videoId) p.set('videoId', videoId);
      if (extra) {
        Object.entries(extra).forEach(([k, v]) => p.set(k, v));
      }
      return p.toString();
    };

    const contentUrl = `/${Studio.content}?${buildStoryParams()}`;
    const creatorUrl = `/${Studio.creator}?${buildStoryParams()}`;

    if (collapsed) {
      return (
        <CollapsedActions>
          <SectionLabelButton
            aria-hidden
            tabIndex={-1}
            style={{ visibility: 'hidden' }}
            type="button"
          >
            Current Story
            <SectionChevron $open aria-hidden>
              <ChevronDownIcon />
            </SectionChevron>
          </SectionLabelButton>
          <StoryNameInfo aria-hidden style={{ visibility: 'hidden' }}>
            {story?.title || 'placeholder'}
          </StoryNameInfo>
          <CollapsedActionList>
            <CollapsedActionLink
              to={contentUrl}
              end
              $disabled={!story}
              $showTopDivider
              onClick={e => {
                if (!story) e.preventDefault();
              }}
            >
              <ContentStudioIcon />
              <CollapsedTooltip>
                {!story ? 'Select a story first' : 'Content Studio'}
              </CollapsedTooltip>
            </CollapsedActionLink>
            {enableCreatorStudioNav && (
              <CollapsedActionLink
                to={creatorUrl}
                end
                $disabled={!story}
                onClick={e => {
                  if (!story) {
                    e.preventDefault();
                    return;
                  }
                  handleVideoEditorClick();
                }}
              >
                <EditVideoIcon />
                <CollapsedTooltip>
                  {!story ? 'Select a story first' : 'Creator Studio'}
                </CollapsedTooltip>
              </CollapsedActionLink>
            )}
          </CollapsedActionList>
        </CollapsedActions>
      );
    }

    if (!story) {
      return (
        <FlatSection $pushDown data-testid="sidebar-empty-story-card">
          <SectionLabelButton
            type="button"
            aria-expanded={currentStoryOpen}
            onClick={() => setCurrentStoryOpen(prev => !prev)}
          >
            Current Story
            <SectionChevron $open={currentStoryOpen} aria-hidden>
              <ChevronDownIcon />
            </SectionChevron>
          </SectionLabelButton>
          <EmptyStoryHint>Loading Story</EmptyStoryHint>
          {currentStoryOpen && (
            <ChildrenContainer>
              <CompactList>
                <DisabledRow role="button" aria-disabled>
                  <CompactIcon>
                    <ContentStudioIcon />
                  </CompactIcon>
                  <span>Content Studio</span>
                </DisabledRow>
                {enableCreatorStudioNav && (
                  <DisabledRow role="button" aria-disabled>
                    <CompactIcon>
                      <EditVideoIcon />
                    </CompactIcon>
                    <span>Creator Studio</span>
                  </DisabledRow>
                )}
              </CompactList>
              <PickStoryLink to={`/${Studio.dashboard}${search}`}>
                Go to Story Manager →
              </PickStoryLink>
            </ChildrenContainer>
          )}
        </FlatSection>
      );
    }

    // Active clip: prefer the Content Studio preview modal clip, fall back to the
    // video currently loaded in Creator Studio's timeline editor.
    const clipPreview = videoCreator.videoClipPreview;
    const currentVideo = videoCreator.currentVideo;
    const activeClip = clipPreview ?? currentVideo ?? null;
    const clipTooltip = activeClip
      ? `Active clip: ${activeClip.title}`
      : undefined;

    return (
      <FlatSection $pushDown>
        <SectionLabelButton
          type="button"
          aria-expanded={currentStoryOpen}
          onClick={() => setCurrentStoryOpen(prev => !prev)}
        >
          Current Story
          <SectionChevron $open={currentStoryOpen} aria-hidden>
            <ChevronDownIcon />
          </SectionChevron>
        </SectionLabelButton>
        <AnimatePresence initial={false}>
          {currentStoryOpen && (
            <fmMotion.div
              key="cs-children"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={SIDEBAR_SPRING}
              style={{ overflow: 'hidden' }}
            >
              <StoryNameInfo
                data-tooltip-id="sidebar-story-name-tooltip"
                data-tooltip-content={story.title}
              >
                {story.title}
              </StoryNameInfo>
              <ChildrenContainer>
                <CompactList>
                  <CompactLink to={contentUrl} end>
                    <CompactIcon>
                      <ContentStudioIcon />
                    </CompactIcon>
                    <span>Content Studio</span>
                  </CompactLink>
                  {enableCreatorStudioNav && (
                    <CompactLink
                      to={creatorUrl}
                      end
                      onClick={handleVideoEditorClick}
                    >
                      <CompactIcon>
                        <EditVideoIcon />
                      </CompactIcon>
                      <span>Creator Studio</span>
                      {clipTooltip && (
                        <CompactInlineTooltip>
                          {clipTooltip}
                        </CompactInlineTooltip>
                      )}
                    </CompactLink>
                  )}
                </CompactList>
              </ChildrenContainer>
            </fmMotion.div>
          )}
        </AnimatePresence>
        <ReactTooltip
          id="sidebar-story-name-tooltip"
          place="right"
          offset={8}
          opacity={1}
          noArrow
          style={COMPACT_TOOLTIP_STYLE}
        />
      </FlatSection>
    );
  });

/* ------------------------------------------------------------------ */
/*  Collapsed view styles                                              */
/* ------------------------------------------------------------------ */

const CollapsedTooltip = styled(SidebarTooltip)``;

const CollapsedActions = styled.div`
  /* Mirrors expanded FlatSection structure exactly so icons below
   * (Brand Kit, etc.) hold their Y position when toggling. No outer gap. */
  display: flex;
  flex-direction: column;
  padding: 4px 8px;
  margin-top: 24px;
`;

const CollapsedActionList = styled.div`
  /* Mirrors the expanded CompactList: gap 4px between action items. */
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CollapsedActionLink = styled(NavLink)<{
  $disabled?: boolean;
  $showTopDivider?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 30px;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: 6px;
  text-decoration: none;
  color: var(--text-secondary);
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  ${p =>
    p.$showTopDivider &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -3px;
        left: 6px;
        right: 6px;
        height: 1px;
        background: var(--sidebar-border);
        pointer-events: none;
      }
    `}

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: var(--sidebar-hover);
    color: var(--text-secondary);
  }

  &:hover ${CollapsedTooltip} {
    opacity: 1;
  }

  &.active {
    background: var(--sidebar-active);
    color: var(--foreground);
  }

  &.active svg {
    color: var(--accent);
  }

  ${p =>
    p.$disabled &&
    css`
      opacity: 0.4;
      pointer-events: auto;
      cursor: not-allowed;
      &:hover {
        background: transparent;
        color: #9ca3af;
      }
    `}
`;

/* ------------------------------------------------------------------ */
/*  Compact (Metal-style) section                                      */
/* ------------------------------------------------------------------ */

const FlatSection = styled.div<{ $pushDown?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 4px 10px 4px;
  margin-top: ${p => (p.$pushDown ? '24px' : '4px')};
`;

const CompactInlineTooltip = styled(SidebarTooltip)``;

const compactRowStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 30px;
  box-sizing: border-box;
  gap: 10px;
  padding: 0 10px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  svg {
    flex-shrink: 0;
  }

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: var(--sidebar-hover);
    color: var(--text-secondary);
  }

  &:hover ${CompactInlineTooltip} {
    opacity: 1;
  }
`;

const SectionLabelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  height: 28px;
  box-sizing: border-box;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 14px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.2px;
  padding: 0 10px 0 12px;
  font-family: inherit;
  white-space: nowrap;
  overflow: hidden;
  transition: color var(--transition-fast) ease;
  animation: sidebarLabelFadeIn 540ms cubic-bezier(0.22, 1, 0.36, 1) 220ms both;

  &:hover {
    color: var(--text-secondary);
  }

  @keyframes sidebarLabelFadeIn {
    from {
      opacity: 0;
      transform: translateX(-4px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const SectionChevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: var(--text-secondary);
  opacity: 1;
  transition: transform var(--transition-fast) ease;
  transform: rotate(${p => (p.$open ? '0deg' : '-90deg')});
`;

const ChildrenContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-left: 22px;

  &::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 2px;
    bottom: 4px;
    width: 1px;
    background: var(--sidebar-border);
  }
`;

const StoryNameInfo = styled.div`
  display: flex;
  align-items: center;
  height: 22px;
  box-sizing: border-box;
  min-width: 0;
  padding: 0 10px 0 12px;
  font-size: 11px;
  line-height: 14px;
  font-weight: var(--font-weight-regular, 400);
  color: var(--text-disabled);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: storyNameInfoFadeIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 300ms both;

  @keyframes storyNameInfoFadeIn {
    from {
      opacity: 0;
      transform: translateX(-4px);
    }
    to {
      opacity: 0.6;
      transform: translateX(0);
    }
  }
`;

const CompactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompactLink = styled(NavLink)`
  ${compactRowStyles}

  &.active {
    background: var(--sidebar-active);
    color: var(--foreground);
  }

  &.active > span:first-child {
    color: var(--accent);
    opacity: 1;
  }
`;

const DisabledRow = styled.div`
  ${compactRowStyles}
  color: var(--text-muted);
  opacity: 0.5;
  cursor: not-allowed;

  &:hover {
    background: transparent;
    color: var(--text-muted);
  }
`;

const CompactIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  opacity: 0.85;
  color: inherit;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyStoryHint = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.4;
  padding: 0 10px 8px 12px;
`;

const PickStoryLink = styled(NavLink)`
  display: block;
  margin: 4px 0 0;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: var(--primary);
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`;
